/**
 * Builds a styled HTML popup content string for map markers.
 * Used by both MapComponent and SearchControl for consistent popup display.
 */
export const buildPopupContent = (props) => {
  const name = props.name || 'Unbekannt';
  
  // Determine type icon and label
  const amenity = (props.amenity || '').toLowerCase();
  const healthcare = (props.healthcare || '').toLowerCase();
  const isDoctor = amenity === 'doctors' || healthcare === 'doctor';
  const isMvz = (props.operator || '').toLowerCase().includes('mvz') || healthcare === 'mvz';
  const isPharmacy = amenity === 'pharmacy' || healthcare === 'pharmacy';
  
  let typeIcon = '🏥';
  let typeLabel = 'Einrichtung';
  if (isMvz) {
    typeIcon = '🏢';
    typeLabel = 'MVZ';
  } else if (isDoctor) {
    typeIcon = '👨‍⚕️';
    typeLabel = 'Arztpraxis';
  } else if (isPharmacy) {
    typeIcon = '💊';
    typeLabel = 'Apotheke';
  }
  
  // Build address
  const street = props['addr:street'] || '';
  const housenumber = props['addr:housenumber'] || '';
  const postcode = props['addr:postcode'] || '';
  const city = props['addr:city'] || '';
  const addressLine1 = [street, housenumber].filter(Boolean).join(' ');
  const addressLine2 = [postcode, city].filter(Boolean).join(' ');
  const hasAddress = addressLine1 || addressLine2;
  
  // Specialty
  const specialty = props['healthcare:speciality'] || '';
  const specialtyFormatted = specialty
    ? specialty.split(';').map(s => s.trim()).filter(Boolean)
        .map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')
    : '';
  
  // Operator
  const operator = props.operator || '';
  
  // Contact info
  const phone = props.phone || props['contact:phone'] || '';
  const email = props.email || props['contact:email'] || '';
  const website = props.website || props['contact:website'] || '';
  
  // Marker color
  const markerColor = props['marker-color'] || '';
  const accentColor = markerColor || '#2563eb';
  
  // Build HTML
  let html = `<div class="popup-card">`;
  
  // Header with colored accent bar
  html += `<div class="popup-header" style="border-left: 4px solid ${accentColor};">`;
  html += `<div class="popup-type-badge" style="background: ${accentColor}20; color: ${accentColor};">`;
  html += `<span class="popup-type-icon">${typeIcon}</span> ${typeLabel}`;
  html += `</div>`;
  html += `<h3 class="popup-name">${name}</h3>`;
  if (specialtyFormatted) {
    html += `<div class="popup-specialty">${specialtyFormatted}</div>`;
  }
  html += `</div>`;
  
  // Details section
  html += `<div class="popup-details">`;
  
  if (operator) {
    html += `<div class="popup-detail-row">`;
    html += `<span class="popup-detail-icon">🏛️</span>`;
    html += `<div class="popup-detail-content">`;
    html += `<span class="popup-detail-label">Betreiber</span>`;
    html += `<span class="popup-detail-value">${operator}</span>`;
    html += `</div></div>`;
  }
  
  if (hasAddress) {
    html += `<div class="popup-detail-row">`;
    html += `<span class="popup-detail-icon">📍</span>`;
    html += `<div class="popup-detail-content">`;
    html += `<span class="popup-detail-label">Adresse</span>`;
    if (addressLine1) html += `<span class="popup-detail-value">${addressLine1}</span>`;
    if (addressLine2) html += `<span class="popup-detail-value">${addressLine2}</span>`;
    html += `</div></div>`;
  }
  
  if (phone) {
    html += `<div class="popup-detail-row">`;
    html += `<span class="popup-detail-icon">📞</span>`;
    html += `<div class="popup-detail-content">`;
    html += `<span class="popup-detail-label">Telefon</span>`;
    html += `<a href="tel:${phone.replace(/[^+\d]/g, '')}" class="popup-detail-link">${phone}</a>`;
    html += `</div></div>`;
  }
  
  if (email) {
    html += `<div class="popup-detail-row">`;
    html += `<span class="popup-detail-icon">✉️</span>`;
    html += `<div class="popup-detail-content">`;
    html += `<span class="popup-detail-label">E-Mail</span>`;
    html += `<a href="mailto:${email}" class="popup-detail-link">${email}</a>`;
    html += `</div></div>`;
  }
  
  if (website) {
    const displayUrl = website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    html += `<div class="popup-detail-row">`;
    html += `<span class="popup-detail-icon">🌐</span>`;
    html += `<div class="popup-detail-content">`;
    html += `<span class="popup-detail-label">Website</span>`;
    html += `<a href="${website}" target="_blank" rel="noopener noreferrer" class="popup-detail-link">${displayUrl}</a>`;
    html += `</div></div>`;
  }
  
  // Nachfolge-Risiko aus dem Crawler (Layer "Praxisnachfolge-Signale")
  const riskBand = props.risk_band;
  if (riskBand) {
    const riskLabels = {
      confirmed: 'Bestätigt', high: 'Hoch', medium: 'Mittel', low: 'Niedrig', none: 'Kein Signal',
    };
    const riskColor = props['marker-color'] || accentColor;
    const signalText = Array.isArray(props.signals)
      ? props.signals.map((s) => s.detail).filter(Boolean).join(', ')
      : '';
    html += `<div class="popup-detail-row">`;
    html += `<span class="popup-detail-icon">🔎</span>`;
    html += `<div class="popup-detail-content">`;
    html += `<span class="popup-detail-label">Nachfolge-Risiko</span>`;
    html += `<span class="popup-detail-value" style="color:${riskColor}; font-weight:600;">${riskLabels[riskBand] || riskBand}</span>`;
    if (signalText) {
      html += `<span class="popup-detail-value" style="font-size:12px; color:#666;">${signalText}</span>`;
    }
    html += `</div></div>`;
  }

  html += `</div>`; // popup-details
  html += `</div>`; // popup-card

  return html;
};

/**
 * Builds a simple address popup for location/address search results.
 */
export const buildAddressPopupContent = (label, properties = {}) => {
  const city = properties.city || '';
  const state = properties.state || '';
  const locationInfo = [city, state].filter(Boolean).join(', ');
  
  let html = `<div class="popup-card">`;
  html += `<div class="popup-header" style="border-left: 4px solid #2563eb;">`;
  html += `<div class="popup-type-badge" style="background: #2563eb20; color: #2563eb;">`;
  html += `<span class="popup-type-icon">📍</span> Adresse`;
  html += `</div>`;
  html += `<h3 class="popup-name">${label}</h3>`;
  if (locationInfo) {
    html += `<div class="popup-specialty">${locationInfo}</div>`;
  }
  html += `</div>`;
  html += `</div>`;
  
  return html;
};
